import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { IMAGES, scrollToId } from "../lib/site";

export const Hero = () => {
  const { t, waLink, lang } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  return (
    <section id="beranda" ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Destinasi wisata Indonesia"
          className="h-[120%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-black/80" />

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-xs font-bold uppercase tracking-[0.3em] text-white/80"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 key={lang} className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-[5.5rem]">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="flex items-center gap-3 rounded-full bg-primary px-7 py-4 font-display text-base font-bold text-primary-foreground transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl"
          >
            <MessageCircle className="h-5 w-5" />
            {t.hero.ctaPrimary}
          </a>
          <button
            onClick={() => scrollToId("armada")}
            data-testid="hero-cta-fleet"
            className="flex items-center gap-3 rounded-full border border-white/40 px-7 py-4 font-display text-base font-bold text-white transition-all duration-200 hover:-translate-y-1 hover:bg-white/10"
          >
            {t.hero.ctaSecondary}
            <ArrowDown className="h-4 w-4" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-16 grid grid-cols-3 gap-6 border-t border-white/20 pt-8"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i}>
              <p className="font-display text-3xl font-bold text-white md:text-5xl">{s.v}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70 md:text-xs">{s.l}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
